import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
/* eslint-disable i18next/no-literal-string */ // Internal loading component, not user-facing text

import * as AbHooks from "abreact/utils/AbHooks";
import { AbBusyIndicator, AbBusyIndicatorSize } from "absdk/commons/ui/misc/AbBusyIndicator";
import { ModuleLoaderUtils } from "abweb/utils/ModuleLoaderUtils";
import * as React from "react";

// JSX '<Foo ...>' syntax apparently supports Foo being any of the underlying ReactRenderableType below

/**
 * Component that will lazily import the underlying component on the first usage, and then delegate to the
 * underlying component instance.   The motivation behind this class is to lighten the exhaustive transitive imports of
 * the underlying component; that module will only be loaded on the first render.  The typical pattern is to
 * separate out the underlying component props into a separate file, and then use that in conjunction with this
 * LazyCmpLoader to ensure that the underlying component imports costs are only incurred on first render.
 * Also see withLazyLoad function (below) that creates a new component class with the importSpec of the
 * underlying component curried away (thereby simplifying the caller api)
 * <p>
 *   The import specs are evaluated only on mount.  If have a rare use case where you need to subsequently change
 *   the import specs for an already mounted lazy component, then set the react 'key' prop to a different value
 *   everytime the import specs change, which will force a new LazyCmpLoader instance.
 */
export function LazyCmpLoader(props) {
  // React weirdness: if setLoadedCmp invoked with a react component class value (aka clazz), react useState machinery complains:
  //     Class constructor cannot be invoked without 'new'
  // Workaround: add a layer of indirection by storing value in object with a 'Cmp' member.
  const [loadedCmp, setLoadedCmp] = React.useState(undefined);
  async function loadCmp() {
    const cmp = await ModuleLoaderUtils.import(props.cmpImportSpec);
    if (cmp == null) {
      // See ABSDK-1545, where case-difference in exportName caused the loaded clazz to be undefined.
      // All modules are in webpack '() => import(hardcodedPath)' that are checked at compile time,
      // so error is probably an invalid exportName.
      //
      // Invalid runtime import specs can't happen when ModuleLoaderUtils.createImportSpec is used to create the IImportSpec
      // as it has strong compile-time type checking (compile error if export not found in the import module)
      // Declarative definitions {importModuleFn: ..., exportName: "foo"} are NOT type checked so add defensive error detection
      throw new Error("LazyLoaderCmp was unable to import react component with export name: '" + props.cmpImportSpec.exportName.toString() + "'.  Note that the export name is case-sensitive.");
    }
    return cmp;
  }
  async function loadHoc(hocFnImportSpec) {
    const hocFn = await ModuleLoaderUtils.import(hocFnImportSpec);
    if (hocFn == null) {
      // Invalid runtime import specs can't happen when ModuleLoaderUtils.createImportSpec is used to create the IImportSpec
      // as it has strong compile-time type checking (compile error if export not found in the import module)
      // Declarative definitions {importModuleFn: ..., exportName: "foo"} are NOT type checked so add defensive error detection
      throw new Error("LazyLoaderCmp was unable to import higher order component function with export name: '" + hocFnImportSpec.exportName.toString() + "'.  Note that the export name is case-sensitive.");
    }
    // At least verify that it is a function (can't verify its arg types or return type, sigh...)
    if (typeof hocFn !== "function") {
      throw new Error("LazyLoaderCmp expected higher order component function with export name: '" + hocFnImportSpec.exportName.toString() + `' to be a function, but was ${typeof hocFn}`);
    }
    return hocFn;
  }
  function buildWrappedComponent(cmp, hocFns) {
    // Safe to call cmp with hocPropsT as they are superset of cmpPropsT.  So cast is safe.
    let wrappedCmp = cmp;
    // Wrap in reverse order so that first hocFn (hocFn[0]) is the outermost layer of the wrappedCmp
    for (let i = hocFns.length - 1; i >= 0; i--) {
      wrappedCmp = hocFns[i](wrappedCmp);
    }
    return wrappedCmp;
  }
  async function loadCmpAndHocFns() {
    const hocFnsP = props.hocFnImportSpecs?.map(hocSpec => loadHoc(hocSpec)) ?? [];
    const [cmp, ...hocFns] = await Promise.all([loadCmp(), ...hocFnsP]); // import in parallel!
    const builtCmp = buildWrappedComponent(cmp, hocFns);
    setLoadedCmp({
      Cmp: builtCmp
    });
  }
  AbHooks.useOnMount(() => {
    loadCmpAndHocFns(); // ignore returned promise!
  });

  // Be careful to keep rendering LoadedCmpClass once it first becomes available (see CAT-864) to avoid unmounts
  return loadedCmp?.Cmp ? /*#__PURE__*/React.createElement(loadedCmp.Cmp, props.cmpProps) : props.loadingIndicator ? /*#__PURE__*/React.createElement(React.Fragment, null, " props.loadingIndicator ") : /*#__PURE__*/React.createElement(AbBusyIndicator, {
    size: AbBusyIndicatorSize.SMALL
  });
}

/**
 * React Higher Order Component: Create a lazy loading version of react function where all of the
 * underlying LazyCmpLoaderProps (mainly importSpec for component and hocFns) except cmpProps are curried away ('baked in').
 * Makes for a simpler api for callers as they only need to provide the
 * cmpProps: HocPropsT props (callers may not even know that underlying component implementation is being lazily loaded).
 */
export function withLazyLoad(lazyProps) {
  // Curries lazy loader props away (except cmpProps) so that callers only need to provide cmpProps: HocPropsT.
  const anonFn = cmpProps => /*#__PURE__*/React.createElement(LazyCmpLoader, _extends({}, lazyProps, {
    cmpProps: cmpProps
  }));
  // Have displayName include exportName for better debugging experience
  anonFn.displayName = `withLazyLoad<${lazyProps.cmpImportSpec.exportName.toString()}>`;
  return anonFn;
}
//# sourceMappingURL=LazyCmpLoader.js.map
